import { connectorCapabilitiesAreActionable } from '@frontend/features/rutero/connectorCapabilitiesQueryKey';

import type { ConnectorInfo } from './queries';

/**
 * Si el proveedor de un circuito puede COTIZAR SALIDAS en esta agencia. Son
 * tres estados distintos y colapsarlos es exactamente el bug que esto arregla
 * (21.18.4): una búsqueda dirigida a un proveedor sin conector configurado
 * termina en `400 CONNECTOR_NOT_CONFIGURED` y la pantalla decía "probá otra
 * ventana de fechas" — el problema no eran las fechas, era el proveedor.
 *
 * - `searchable`  — hay un conector de circuitos configurado para ese
 *   proveedor y no declara que no busca.
 * - `unavailable` — la autoridad de conectores ya contestó y ese proveedor no
 *   está: sin conector para este ownership, sin credenciales, o declarando
 *   `search: false`. Es lo mismo que el hub responde `CONNECTOR_NOT_CONFIGURED`,
 *   dicho ANTES del click.
 * - `unknown`     — todavía no se sabe (la lista de conectores no cargó, o el
 *   circuito viene de un link viejo sin proveedor).
 */
export type CircuitDepartureAvailability =
  | 'searchable'
  | 'unavailable'
  | 'unknown';

/** El vertical del hub al que pertenecen los conectores que cotizan salidas. */
const CIRCUITS_VERTICAL = 'circuits';

export interface ConnectorAuthority {
  isSuccess: boolean;
  isFetching: boolean;
}

/**
 * NINGUNO de los tres estados bloquea: esto es un aviso, no un gate. La lista
 * de conectores se sirve cacheada (10 min en el backend, 30 min en el cliente)
 * y puede no haber visto credenciales recién cargadas, así que la autoridad
 * final de quién cotiza es el hub: la búsqueda dirigida se manda igual y, si
 * de verdad no está, su `400 CONNECTOR_NOT_CONFIGURED` trae el MISMO estado
 * explícito. Bloquear con información posiblemente vieja escondería salidas
 * que sí existen; el estado se dice, no se impone.
 *
 * `provenSearchable` es la otra cara de eso: el proveedor que ACABA de
 * contestar una búsqueda dirigida. Un resultado autoritativo en pantalla le
 * gana a cualquier snapshot cacheado (21.23) — seguir avisando "no se puede
 * cotizar" arriba de las salidas cotizadas sería contradecir lo que el asesor
 * está leyendo.
 */
export function resolveCircuitDepartureAvailability({
  connectors,
  authority,
  provider,
  provenSearchable,
}: {
  connectors: readonly ConnectorInfo[] | undefined;
  authority: ConnectorAuthority;
  provider?: string;
  provenSearchable?: string;
}): CircuitDepartureAvailability {
  if (!provider) return 'unknown';
  if (provider === provenSearchable) return 'searchable';
  if (!connectorCapabilitiesAreActionable(authority)) return 'unknown';

  const matches = (connectors ?? []).filter(
    (connector) =>
      connector.vertical === CIRCUITS_VERTICAL && connector.provider === provider,
  );
  // Ausente de la lista es el caso REAL de vpt: el catálogo rastreado lo
  // publica igual (rastrear no es cotizar), así que "no está" no es una
  // anomalía sino el estado que hay que contar.
  if (!matches.length) return 'unavailable';

  // Un conector sin credenciales para este ownership no participa del fan-out
  // (`configured: false`), y uno que declara `search: false` no cotiza. Las
  // `availableCapabilities` ya cruza soporte, configuración y bloqueos
  // externos. La ausencia de ese campo en una respuesta rota no habilita ni
  // deshabilita: queda desconocida y falla cerrado en el CTA.
  if (
    matches.some(
      (connector) =>
        connector.configured && connector.availableCapabilities?.search === true,
    )
  ) {
    return 'searchable';
  }
  if (
    matches.every(
      (connector) =>
        !connector.configured || connector.availableCapabilities?.search === false,
    )
  ) {
    return 'unavailable';
  }
  return 'unknown';
}

/**
 * Los proveedores de una lista del catálogo cuyos circuitos NO se pueden
 * cotizar. Se resuelve por PROVEEDOR y no por entrada porque el estado es del
 * conector: todas las entradas de vpt comparten el mismo destino.
 */
export function unsearchableCircuitProviders({
  providers,
  connectors,
  authority,
  provenSearchable,
}: {
  providers: readonly string[];
  connectors: readonly ConnectorInfo[] | undefined;
  authority: ConnectorAuthority;
  provenSearchable?: string;
}): string[] {
  return providers.filter(
    (provider) =>
      resolveCircuitDepartureAvailability({
        connectors,
        authority,
        provider,
        provenSearchable,
      }) === 'unavailable',
  );
}
